import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Globe, Shield, Zap, ChevronDown, ArrowRight, Star,
  CheckCircle, DollarSign, Activity, Menu, X, Wallet,
  Code, Terminal, Building, Phone, Send, Lock, Users,
  Copy
} from 'lucide-react';

/* ─── THEME ─────────────────────────────────────────────── */
// Crossmint-inspired: deep navy bg, gold primary, clean enterprise feel
const P  = '#C29500';   // gold primary
const P2 = '#D4A800';   // gold light
const G  = '#E8C040';
const C  = '#06B6D4';   // cyan accent

/* ─── DATA ─────────────────────────────────────────────── */
const STATS = [
  { value:'180+',   label:'Countries Supported',    color: P2  },
  { value:'$4.2M+', label:'Total Settled',            color: G   },
  { value:'50K+',   label:'Payouts Processed',        color: C   },
  { value:'99.9%',  label:'Settlement Success Rate',  color:'#F59E0B' },
];

const CURRENCIES = [
  { flag:'🇳🇬', code:'NGN', name:'Nigerian Naira',   method:'Bank Transfer', time:'< 5 min' },
  { flag:'🇬🇭', code:'GHS', name:'Ghanaian Cedi',    method:'Bank / MoMo',   time:'< 3 min' },
  { flag:'🌍',  code:'XOF', name:'West African CFA', method:'Flooz / MoMo',  time:'< 5 min' },
  { flag:'🇰🇪', code:'KES', name:'Kenyan Shilling',  method:'M-Pesa',        time:'Instant' },
  { flag:'🇺🇬', code:'UGX', name:'Ugandan Shilling', method:'MTN MoMo',      time:'< 5 min' },
  { flag:'🇸🇳', code:'XOF', name:'Senegal / CFA',    method:'Wave / Orange', time:'< 5 min' },
];

const PAYOUT_FEED = [
  { name:'Emmanuel K.', flag:'🇳🇬', amount:'$240', method:'GTBank NGN',    time:'2m ago'  },
  { name:'Fatima A.',   flag:'🇬🇭', amount:'$180', method:'MTN MoMo GHS', time:'5m ago'  },
  { name:'David M.',    flag:'🇰🇪', amount:'$320', method:'M-Pesa KES',   time:'12m ago' },
  { name:'Amara S.',    flag:'🌍',  amount:'$150', method:'Flooz XOF',    time:'18m ago' },
  { name:'Grace O.',    flag:'🇺🇬', amount:'$200', method:'MTN MoMo UGX', time:'25m ago' },
  { name:'Kwame B.',    flag:'🇬🇭', amount:'$410', method:'Ecobank GHS',  time:'31m ago' },
];

const COLLECTION_FEED = [
  { from:'Shopify Store',   token:'USDT', amount:'+$1,240', network:'TRC20', time:'Just now' },
  { from:'API Integration', token:'USDC', amount:'+$3,800', network:'ERC20', time:'2m ago'   },
  { from:'Payment Link',    token:'USDT', amount:'+$520',   network:'BEP20', time:'5m ago'   },
  { from:'Web Widget',      token:'USDT', amount:'+$980',   network:'TRC20', time:'11m ago'  },
];

const PLANS = [
  {
    name:'Starter', price:29, color: P, popular:false,
    features:['5 Collection Links','USDT & USDC','Bank Payout (NGN)','Email Alerts','Basic Dashboard','Community Support'],
  },
  {
    name:'Pro', price:59, color: C, popular:true,
    features:['Unlimited Collections','USDT, USDC & More','Bank & MoMo Payout','Webhook & API Access','Telegram + Email Alerts','Priority Support','Advanced Analytics'],
  },
  {
    name:'Elite', price:99, color:'#F59E0B', popular:false,
    features:['Unlimited Everything','All Stablecoins','All Payout Methods','Full API Access','Telegram + Email Alerts','VIP Support','Custom Integration','White-label Ready'],
  },
];

const TESTIMONIALS = [
  { name:'Emmanuel K.', country:'🇬🇭 Ghana',   text:'Kudibot makes it easy to collect USDT from my clients globally and get paid in GHS to my bank same day. The whole process is seamless!',              profit:'$4,200 collected',  plan:'Pro'     },
  { name:'Fatima A.',   country:'🇳🇬 Nigeria', text:'I run an e-commerce store and Kudibot handles all my USDT collections. MTN MoMo settlements work perfectly every time — always on time!',           profit:'$8,900 settled',    plan:'Starter' },
  { name:'David M.',    country:'🇰🇪 Kenya',   text:'M-Pesa payout is instant! I collect stablecoin payments from overseas clients and receive KES within minutes. Best payout partner in Africa!',       profit:'$12,100 paid out',  plan:'Elite'   },
  { name:'Amara S.',    country:'🇸🇳 Senegal', text:'Running a freelance agency, Kudibot lets me collect USDC from US clients and pay my team in XOF via Wave. A complete game changer for us!',         profit:'$6,500 processed',  plan:'Pro'     },
  { name:'Grace O.',    country:'🇺🇬 Uganda',  text:'MTN MoMo Uganda works perfectly! I receive my USDT collections converted and settled within minutes. Kudibot never lets me down!',                   profit:'$3,780 received',   plan:'Starter' },
  { name:'Kwame B.',    country:'🇬🇭 Ghana',   text:'Elite plan is worth every penny. I collect from 12 countries and pay out to 6 African markets. The API integration is rock solid!',                  profit:'$28,200 volume',    plan:'Elite'   },
];

const FAQS = [
  { q:'What is Kudibot?',                        a:'Kudibot is a stablecoin collection and payout platform. We help businesses and individuals collect USDT and USDC from customers globally and settle funds to local bank accounts or mobile money wallets across 180+ countries.' },
  { q:'Which stablecoins do you support?',        a:'We currently support USDT (TRC20, ERC20, BEP20) and USDC (ERC20, Polygon). More stablecoins are added regularly based on demand.' },
  { q:'How does stablecoin collection work?',     a:'Create a payment link or integrate our API. Your customers pay in USDT or USDC. Funds hit your Kudibot wallet instantly. You can then withdraw to your local bank or mobile money at any time.' },
  { q:'Which payout methods are supported?',      a:'Nigeria: Bank Transfer (NGN). Ghana: Bank Transfer & MTN/Vodafone/AT MoMo (GHS). West Africa: Flooz Money (XOF). Kenya: M-Pesa (KES). Uganda: MTN MoMo (UGX). More countries added regularly.' },
  { q:'How fast are payouts?',                    a:'Most payouts arrive within 1–5 minutes. Bank transfers may take up to 30 minutes depending on the receiving bank. Mobile money is typically instant.' },
  { q:'Are there fees?',                          a:'We charge a small percentage on each collection and payout. Starter: 1.5%. Pro: 1.0%. Elite: 0.75%. No hidden charges, no monthly surprises beyond your plan fee.' },
  { q:'Can developers integrate Kudibot?',        a:'Yes! Kudibot offers a REST API for businesses to integrate stablecoin collection and global payouts into their apps. Send payouts to NGN, GHS, XOF and more with a single API call. Contact us for API access.' },
];

const CODE_SAMPLE = `// Kudibot Payout API
const response = await fetch(
  'https://api.kudibot.io/v1/payout',
  {
    method  : 'POST',
    headers : {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type' : 'application/json',
    },
    body: JSON.stringify({
      amount      : 100,
      currency    : 'NGN',
      method      : 'bank',
      bank_code   : '000014',
      account_no  : '0123456789',
      account_name: 'John Doe',
    }),
  }
);

const { reference, status, amount_local }
  = await response.json();
// { reference: 'KDB-ABC123',
//   status: 'success',
//   amount_local: 158000 }`;

const TRUST_LOGOS = ['Stripe', 'Shopify', 'WooCommerce', 'Flutterwave', 'Paystack', 'Binance Pay'];

/* ─── LOGO ──────────────────────────────────────────────── */
const KudiLogo = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="kudi-grad" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#E8C040"/>
        <stop offset="100%" stopColor="#C29500"/>
      </linearGradient>
    </defs>
    <rect width="80" height="80" rx="20" fill="url(#kudi-grad)"/>
    <rect x="20" y="18" width="9" height="44" rx="4.5" fill="#fff"/>
    <path d="M29 40 L54 20" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M29 40 L54 60" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M42 56 L54 60 L50 48" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ─── HELPERS ───────────────────────────────────────────── */
const Tag = ({ children, color = P }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full"
    style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
    {children}
  </span>
);

const GradientText = ({ children, className = '' }) => (
  <span className={className} style={{
    background: `linear-gradient(135deg, ${P2}, ${C})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  }}>{children}</span>
);

/* ─── COMPONENT ─────────────────────────────────────────── */
export default function Landing() {
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [openFaq,   setOpenFaq]   = useState(null);
  const [payoutIdx, setPayoutIdx] = useState(0);
  const [copied,    setCopied]    = useState(false);

  useEffect(() => {
    const t = setInterval(() => setPayoutIdx(p => (p + 1) % PAYOUT_FEED.length), 2500);
    return () => clearInterval(t);
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(CODE_SAMPLE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* shared card style */
  const card = {
    background: 'rgba(194,149,0,0.04)',
    border: '1px solid rgba(194,149,0,0.14)',
    borderRadius: 16,
  };

  return (
    <div style={{ background: '#060A18', color: '#fff', fontFamily: 'Inter, sans-serif', overflowX: 'hidden' }}>

      {/* ══ NAVBAR ══ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: 'rgba(6,10,24,0.92)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(194,149,0,0.12)',
      }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '0 24px', height: 64,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ filter: `drop-shadow(0 0 10px ${P}60)` }}>
              <KudiLogo size={34} />
            </div>
            <span style={{ fontWeight: 800, fontSize: 18, letterSpacing: -0.5 }}>
              Kudi<span style={{ color: P2 }}>bot</span>
            </span>
          </div>

          {/* Links */}
          <div className="hidden md:flex" style={{ gap: 32 }}>
            {[['Payouts','#payouts'],['Features','#features'],['Developers','#developers'],['Pricing','#pricing'],['FAQ','#faq']].map(([l,h]) => (
              <a key={l} href={h} style={{ color: '#94A3B8', fontSize: 14, fontWeight: 500, textDecoration: 'none', transition: 'color .2s' }}
                onMouseOver={e=>e.target.style.color='#fff'} onMouseOut={e=>e.target.style.color='#94A3B8'}>
                {l}
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:flex" style={{ gap: 12, alignItems: 'center' }}>
            <Link to="/login" style={{ color: '#94A3B8', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>
              Sign in
            </Link>
            <Link to="/register">
              <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                style={{
                  padding: '9px 20px', borderRadius: 100, border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${P}, ${C})`,
                  color: '#fff', fontWeight: 700, fontSize: 14,
                  boxShadow: `0 0 24px ${P}40`,
                }}>
                Get Started →
              </motion.button>
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden"
            style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', padding: 4 }}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{ borderTop: '1px solid rgba(194,149,0,0.12)', padding: '16px 24px' }}>
              {[['Payouts','#payouts'],['Features','#features'],['Developers','#developers'],['Pricing','#pricing'],['FAQ','#faq']].map(([l,h]) => (
                <a key={l} href={h} onClick={() => setMenuOpen(false)}
                  style={{ display: 'block', color: '#94A3B8', fontSize: 14, fontWeight: 500,
                    textDecoration: 'none', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  {l}
                </a>
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <Link to="/login" onClick={() => setMenuOpen(false)} style={{
                  flex: 1, textAlign: 'center', padding: '11px 0', borderRadius: 10,
                  border: '1px solid rgba(194,149,0,0.25)', color: '#fff', fontSize: 14,
                  fontWeight: 600, textDecoration: 'none',
                }}>Sign in</Link>
                <Link to="/register" onClick={() => setMenuOpen(false)} style={{
                  flex: 1, textAlign: 'center', padding: '11px 0', borderRadius: 10,
                  background: `linear-gradient(135deg, ${P}, ${C})`,
                  color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none',
                }}>Get Started</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ══ HERO ══ */}
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
        padding: '120px 24px 80px', position: 'relative', overflow: 'hidden' }}>

        {/* BG glow */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: '10%', left: '20%', width: 600, height: 600,
            background: `radial-gradient(circle, ${P}18 0%, transparent 65%)`, borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: '10%', right: '10%', width: 400, height: 400,
            background: `radial-gradient(circle, ${C}12 0%, transparent 65%)`, borderRadius: '50%' }} />
          {/* subtle grid */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.025,
            backgroundImage: `linear-gradient(${P} 1px, transparent 1px), linear-gradient(90deg, ${P} 1px, transparent 1px)`,
            backgroundSize: '64px 64px' }} />
        </div>

        <div style={{ maxWidth: 1140, margin: '0 auto', width: '100%', position: 'relative' }}>
          <div className="grid lg:grid-cols-2" style={{ gap: 64, alignItems: 'center' }}>

            {/* Left */}
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 24 }}>
                <Tag color={G}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: G, display: 'inline-block', animation: 'pulse 2s infinite' }} />
                  Live · Stablecoin Collection & Payout Partner
                </Tag>
              </motion.div>

              <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                style={{ fontSize: 'clamp(40px, 5vw, 64px)', fontWeight: 900, lineHeight: 1.06,
                  letterSpacing: -2, marginBottom: 24 }}>
                Your Stablecoin<br />
                <GradientText>Collection & Payout</GradientText><br />
                Partner.
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                style={{ fontSize: 18, color: '#94A3B8', lineHeight: 1.7, marginBottom: 36, maxWidth: 480 }}>
                Accept <strong style={{ color: '#fff' }}>USDT & USDC</strong> from customers worldwide.
                Settle to <strong style={{ color: P2 }}>local banks & mobile wallets</strong> across
                Africa in minutes — with a single API call.
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 40 }}>
                <Link to="/register">
                  <motion.button whileHover={{ scale: 1.04, boxShadow: `0 0 40px ${P}50` }}
                    whileTap={{ scale: 0.96 }}
                    style={{ padding: '14px 28px', borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg, ${P}, ${C})`,
                      color: '#fff', fontWeight: 700, fontSize: 15,
                      display: 'flex', alignItems: 'center', gap: 8,
                      boxShadow: `0 0 28px ${P}40` }}>
                    Start for Free <ArrowRight size={16} />
                  </motion.button>
                </Link>
                <a href="#developers">
                  <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    style={{ padding: '14px 28px', borderRadius: 12, cursor: 'pointer',
                      background: 'rgba(194,149,0,0.08)', border: `1px solid ${P}30`,
                      color: '#fff', fontWeight: 600, fontSize: 15,
                      display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Code size={16} color={P2} /> View API Docs
                  </motion.button>
                </a>
              </motion.div>

              {/* Trust pills */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                {[
                  [Building, 'Bank Transfers'], [Phone, 'Mobile Money'],
                  [Globe, '180+ Countries'], [Lock, 'USDT & USDC'],
                ].map(([Icon, label], i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 13, color: '#64748B' }}>
                    <Icon size={13} color={P2} /> {label}
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right — dual cards */}
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Live payout feed */}
              <div style={{ ...card, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Globe size={14} color={C} />
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Live Payout Feed</span>
                  </div>
                  <Tag color={G}><span style={{width:6,height:6,borderRadius:'50%',background:G,display:'inline-block'}}/>LIVE</Tag>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div key={payoutIdx}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', background: 'rgba(255,255,255,0.03)',
                      borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 24 }}>{PAYOUT_FEED[payoutIdx].flag}</span>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 13, margin: 0 }}>{PAYOUT_FEED[payoutIdx].name}</p>
                        <p style={{ color: '#64748B', fontSize: 11, margin: 0 }}>
                          {PAYOUT_FEED[payoutIdx].method} · {PAYOUT_FEED[payoutIdx].time}
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: G, fontWeight: 800, fontSize: 15, margin: 0 }}>{PAYOUT_FEED[payoutIdx].amount}</p>
                      <span style={{ fontSize: 10, background: `${G}18`, color: G,
                        padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>SETTLED ✓</span>
                    </div>
                  </motion.div>
                </AnimatePresence>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {[['🇳🇬','NGN'],['🇬🇭','GHS'],['🌍','XOF'],['🇰🇪','KES'],['🇺🇬','UGX'],['🌐','More']].map(([f,l],i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                      background: i < 5 ? `${P}0a` : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${i < 5 ? P+'25' : 'rgba(255,255,255,0.06)'}`,
                      color: i < 5 ? P2 : '#64748B' }}>
                      {f} {l}
                    </div>
                  ))}
                </div>
              </div>

              {/* Collections card */}
              <div style={{ ...card, padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Wallet size={14} color={P2} />
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Incoming Collections</span>
                  </div>
                  <Tag color={P}>USDT · USDC</Tag>
                </div>
                {COLLECTION_FEED.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '9px 12px', background: 'rgba(255,255,255,0.02)',
                    borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6,
                        background: `${G}18`, color: G, fontWeight: 700 }}>{c.token}</span>
                      <div>
                        <p style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>{c.from}</p>
                        <p style={{ fontSize: 10, color: '#64748B', margin: 0 }}>{c.network} · {c.time}</p>
                      </div>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 800, color: G }}>{c.amount}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ TRUST STRIP ══ */}
      <div style={{ borderTop: '1px solid rgba(194,149,0,0.1)', borderBottom: '1px solid rgba(194,149,0,0.1)',
        padding: '20px 24px', background: 'rgba(194,149,0,0.03)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', display: 'flex', alignItems: 'center',
          gap: 40, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span style={{ fontSize: 12, color: '#475569', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
            Integrates with
          </span>
          {TRUST_LOGOS.map((l, i) => (
            <span key={i} style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: -0.3 }}>{l}</span>
          ))}
        </div>
      </div>

      {/* ══ STATS ══ */}
      <section style={{ padding: '80px 24px', background: 'rgba(194,149,0,0.02)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 32 }}>
          {STATS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }} style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 40, fontWeight: 900, margin: '0 0 6px', color: s.color,
                letterSpacing: -1 }}>{s.value}</p>
              <p style={{ fontSize: 14, color: '#64748B', margin: 0 }}>{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section style={{ padding: '100px 24px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Tag color={P} style={{ marginBottom: 16 }}>How It Works</Tag>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, letterSpacing: -1.5,
                margin: '16px 0 16px' }}>
                Three steps to <GradientText>global settlement</GradientText>
              </h2>
              <p style={{ fontSize: 17, color: '#64748B', maxWidth: 520, margin: '0 auto' }}>
                From stablecoin collection to local currency payout — automated, fast and reliable.
              </p>
            </motion.div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {[
              { step:'01', icon:<Wallet size={22}/>, color: P, title:'Deposit or Collect',
                desc:'Fund your Kudibot wallet with USDT/USDC via TRC20, ERC20 or BEP20. Or create a payment link and let customers pay you directly in stablecoin.' },
              { step:'02', icon:<DollarSign size={22}/>, color: C, title:'Request Payout',
                desc:'Choose your target currency (NGN, GHS, XOF, KES…), select bank transfer or mobile money, and enter recipient details.' },
              { step:'03', icon:<Send size={22}/>, color: G, title:'Funds Arrive in Minutes',
                desc:'We convert at live FX rates and deliver directly to the bank account or mobile money wallet — usually within 1–5 minutes.' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                style={{ ...card, padding: '28px 24px', textAlign: 'center',
                  transition: 'border-color .25s', cursor: 'default' }}
                onMouseOver={e => e.currentTarget.style.borderColor = `${s.color}40`}
                onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(194,149,0,0.14)'}>
                <div style={{ width: 52, height: 52, borderRadius: 14, margin: '0 auto 16px',
                  background: `${s.color}18`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: s.color }}>{s.icon}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: s.color, letterSpacing: 2,
                  marginBottom: 10 }}>{s.step}</div>
                <h3 style={{ fontWeight: 800, fontSize: 17, margin: '0 0 10px' }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ GLOBAL PAYOUTS ══ */}
      <section id="payouts" style={{ padding: '100px 24px',
        background: 'rgba(194,149,0,0.03)', borderTop: '1px solid rgba(194,149,0,0.1)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Tag color={C}>Global Payout Infrastructure</Tag>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, letterSpacing: -1.5,
                margin: '16px 0 16px' }}>
                Send Money to Any <GradientText>Bank or Mobile Wallet</GradientText>
              </h2>
              <p style={{ fontSize: 17, color: '#64748B', maxWidth: 560, margin: '0 auto' }}>
                Convert USDT & USDC to local currency and deliver to banks or mobile money wallets across Africa — in minutes.
              </p>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2" style={{ gap: 48 }}>
            {/* Currency list */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h3 style={{ fontWeight: 800, fontSize: 18, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Globe size={18} color={C} /> Supported Currencies & Methods
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {CURRENCIES.map((c, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                    style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', transition: 'border-color .2s' }}
                    onMouseOver={e => e.currentTarget.style.borderColor = `${P}40`}
                    onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(194,149,0,0.14)'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 22 }}>{c.flag}</span>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 14, margin: 0 }}>{c.name}</p>
                        <p style={{ color: '#64748B', fontSize: 12, margin: 0 }}>{c.method}</p>
                      </div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px',
                      borderRadius: 20, background: `${G}18`, color: G }}>{c.time}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Live feed + feature pills */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h3 style={{ fontWeight: 800, fontSize: 18, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Activity size={18} color={G} /> Live Payout Feed
              </h3>
              <div style={{ ...card, padding: 20, marginBottom: 16 }}>
                {PAYOUT_FEED.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', background: 'rgba(255,255,255,0.02)',
                    borderRadius: 10, border: '1px solid rgba(255,255,255,0.05)', marginBottom: i < 5 ? 8 : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18 }}>{p.flag}</span>
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>{p.name}</p>
                        <p style={{ color: '#64748B', fontSize: 11, margin: 0 }}>{p.method}</p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ color: G, fontWeight: 800, fontSize: 14, margin: 0 }}>{p.amount}</p>
                      <p style={{ color: '#475569', fontSize: 11, margin: 0 }}>{p.time}</p>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, color: '#64748B' }}>Total paid today</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: G }}>$1,500+</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  [Building, 'Bank Transfers', 'NGN, GHS, KES'],
                  [Phone, 'Mobile Money', 'MTN, Flooz, M-Pesa'],
                  [Shield, 'Secure & Fast', 'Encrypted payouts'],
                  [Zap, 'Live FX Rates', 'Best rates always'],
                ].map(([Icon, title, desc], i) => (
                  <div key={i} style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ color: P2, marginTop: 2 }}><Icon size={15} /></div>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 13, margin: '0 0 2px' }}>{title}</p>
                      <p style={{ color: '#64748B', fontSize: 12, margin: 0 }}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/register">
              <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                style={{ padding: '14px 32px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${C}, ${P})`,
                  color: '#fff', fontWeight: 700, fontSize: 15,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  boxShadow: `0 0 28px ${C}40` }}>
                Start Sending Payouts <ArrowRight size={16} />
              </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* ══ STABLECOIN FEATURES ══ */}
      <section id="features" style={{ padding: '100px 24px',
        borderTop: '1px solid rgba(194,149,0,0.1)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Tag color={P}>Stablecoin Infrastructure</Tag>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, letterSpacing: -1.5,
                margin: '16px 0 16px' }}>
                Collect Globally. <GradientText>Settle Locally.</GradientText>
              </h2>
              <p style={{ fontSize: 17, color: '#64748B', maxWidth: 520, margin: '0 auto' }}>
                Everything you need to accept stablecoins and pay out to any bank or mobile wallet — built for Africa.
              </p>
            </motion.div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {[
              { icon:<DollarSign size={20}/>, color: P, title:'Stablecoin Collection',
                desc:'Accept USDT & USDC via TRC20, ERC20, BEP20 with payment links, widgets or API. Instant wallet credit.' },
              { icon:<Globe size={20}/>, color: C, title:'180+ Country Coverage',
                desc:'Settle to NGN, GHS, XOF, KES, UGX and more. Bank transfers and mobile money across Africa.' },
              { icon:<Zap size={20}/>, color:'#F59E0B', title:'Instant Settlement',
                desc:'1–5 minute payouts. Mobile money is typically instant. No waiting days for funds to arrive.' },
              { icon:<Shield size={20}/>, color: G, title:'Secure & Compliant',
                desc:'Bank-grade encryption. KYC-verified merchants. Full audit trail and reconciliation reports.' },
              { icon:<Code size={20}/>, color: P2, title:'Developer-First API',
                desc:'Clean REST API with webhooks, SDKs and comprehensive docs. Go live in minutes.' },
              { icon:<Send size={20}/>, color:'#F87171', title:'Real-Time Alerts',
                desc:'Instant Telegram and email notifications on every collection received and payout settled.' },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                style={{ ...card, padding: '24px 20px', transition: 'border-color .25s' }}
                onMouseOver={e => e.currentTarget.style.borderColor = `${f.color}40`}
                onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(194,149,0,0.14)'}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${f.color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: f.color, marginBottom: 16 }}>{f.icon}</div>
                <h4 style={{ fontWeight: 800, fontSize: 15, margin: '0 0 8px' }}>{f.title}</h4>
                <p style={{ fontSize: 13, color: '#64748B', lineHeight: 1.65, margin: 0 }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ DEVELOPER API ══ */}
      <section id="developers" style={{ padding: '100px 24px',
        background: 'rgba(194,149,0,0.03)', borderTop: '1px solid rgba(194,149,0,0.1)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div className="grid lg:grid-cols-2" style={{ gap: 64, alignItems: 'center' }}>

            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Tag color={P2} style={{ marginBottom: 20 }}><Terminal size={11} /> Developer API</Tag>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, letterSpacing: -1.5,
                margin: '16px 0 20px' }}>
                Integrate Global Payouts<br /><GradientText>in Minutes</GradientText>
              </h2>
              <p style={{ fontSize: 17, color: '#64748B', lineHeight: 1.7, marginBottom: 32 }}>
                Build stablecoin collection and payout infrastructure into your app with the Kudibot REST API.
                A single call sends money to banks and mobile wallets across Africa.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 32 }}>
                {[
                  [Zap, P, 'Simple REST API', 'Clean JSON API with webhooks, SDKs and full documentation.'],
                  [Globe, C, 'Multi-Currency', 'NGN, GHS, XOF, KES, UGX and more. Live FX rates on every call.'],
                  [Shield, G, 'Secure & Reliable', 'API key auth, webhook signatures, 99.9% uptime SLA.'],
                  [Users, '#F59E0B', 'Business Ready', 'Bulk payouts, reconciliation reports, dedicated support.'],
                ].map(([Icon, color, title, desc], i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: `${color}18`, color, display: 'flex',
                      alignItems: 'center', justifyContent: 'center' }}><Icon size={16} /></div>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 14, margin: '0 0 2px' }}>{title}</p>
                      <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="mailto:api@kudibot.io">
                  <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    style={{ padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg, ${P}, ${C})`,
                      color: '#fff', fontWeight: 700, fontSize: 14,
                      display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Code size={15} /> Get API Access
                  </motion.button>
                </a>
                <Link to="/register">
                  <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    style={{ padding: '12px 24px', borderRadius: 10, cursor: 'pointer',
                      background: 'rgba(194,149,0,0.08)', border: `1px solid ${P}30`,
                      color: '#fff', fontWeight: 600, fontSize: 14 }}>
                    View Docs →
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            {/* Code block */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(194,149,0,0.2)',
                background: '#0D1117' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', background: '#161B22',
                  borderBottom: '1px solid rgba(194,149,0,0.15)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#FF5F57' }} />
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#FFBD2E' }} />
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#28CA41' }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#64748B', fontFamily: 'monospace', marginLeft: 8 }}>payout.js</span>
                  </div>
                  <button onClick={copyCode}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none',
                      border: 'none', cursor: 'pointer', color: copied ? G : '#64748B', fontSize: 12 }}>
                    <Copy size={12} /> {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div style={{ padding: '20px 20px', overflowX: 'auto' }}>
                  <pre style={{ fontSize: 12, fontFamily: 'monospace', lineHeight: 1.7, margin: 0 }}>
                    {CODE_SAMPLE.split('\n').map((line, i) => (
                      <div key={i} style={{ display: 'flex' }}>
                        <span style={{ color: '#3B4252', width: 24, flexShrink: 0, textAlign: 'right', marginRight: 16, userSelect: 'none' }}>{i + 1}</span>
                        <span style={{
                          color: line.includes('//') ? '#6B7280'
                            : line.includes("'") || line.includes('"') ? '#A3E635'
                            : line.includes('const') || line.includes('await') || line.includes('=') ? C
                            : line.includes('{') || line.includes('}') ? P2
                            : '#E2E8F0'
                        }}>{line}</span>
                      </div>
                    ))}
                  </pre>
                </div>
                <div style={{ padding: '0 20px 16px' }}>
                  <div style={{ background: `${G}12`, border: `1px solid ${G}25`,
                    borderRadius: 10, padding: '10px 14px' }}>
                    <span style={{ color: G, fontSize: 12, fontFamily: 'monospace', fontWeight: 600 }}>
                      ✓ Payout successful · KDB-ABC123 · ₦158,000 sent
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ PRICING ══ */}
      <section id="pricing" style={{ padding: '100px 24px', borderTop: '1px solid rgba(194,149,0,0.1)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Tag color={P}>Pricing</Tag>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, letterSpacing: -1.5,
                margin: '16px 0 12px' }}>
                Simple, <GradientText>Transparent</GradientText> Pricing
              </h2>
              <p style={{ fontSize: 16, color: '#64748B' }}>Pay monthly. Cancel anytime. No hidden fees.</p>
            </motion.div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
            {PLANS.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                style={{
                  ...card, padding: '28px 24px', position: 'relative',
                  ...(p.popular ? { borderColor: `${C}50`, transform: 'scale(1.03)',
                    boxShadow: `0 0 40px ${C}18` } : {}),
                }}>
                {p.popular && (
                  <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)' }}>
                    <span style={{ padding: '4px 16px', borderRadius: 100, fontSize: 11, fontWeight: 800,
                      background: `linear-gradient(135deg, ${P}, ${C})`, color: '#fff' }}>
                      ⭐ MOST POPULAR
                    </span>
                  </div>
                )}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                  fontWeight: 700, padding: '4px 12px', borderRadius: 20, marginBottom: 20,
                  background: `${p.color}18`, color: p.color }}>
                  {p.name} Plan
                </div>
                <div style={{ marginBottom: 24 }}>
                  <span style={{ fontSize: 44, fontWeight: 900, letterSpacing: -2 }}>${p.price}</span>
                  <span style={{ fontSize: 14, color: '#64748B' }}>/month</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 }}>
                  {p.features.map((f, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <CheckCircle size={14} color={p.color} style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 14, color: '#CBD5E1' }}>{f}</span>
                    </div>
                  ))}
                </div>
                <Link to="/register">
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
                      cursor: 'pointer', fontWeight: 700, fontSize: 14,
                      ...(p.popular
                        ? { background: `linear-gradient(135deg, ${P}, ${C})`, color: '#fff' }
                        : { background: `${p.color}12`, border: `1px solid ${p.color}25`, color: p.color }) }}>
                    Get Started →
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══ */}
      <section id="testimonials" style={{ padding: '100px 24px',
        background: 'rgba(194,149,0,0.03)', borderTop: '1px solid rgba(194,149,0,0.1)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Tag color={G}>Testimonials</Tag>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, letterSpacing: -1.5,
                margin: '16px 0 12px' }}>
                Businesses <GradientText>Love Kudibot</GradientText>
              </h2>
              <p style={{ fontSize: 16, color: '#64748B' }}>Real results from real users across Africa and beyond</p>
            </motion.div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                style={{ ...card, padding: '24px', display: 'flex', flexDirection: 'column',
                  transition: 'border-color .25s' }}
                onMouseOver={e => e.currentTarget.style.borderColor = `${P}35`}
                onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(194,149,0,0.14)'}>
                <div style={{ display: 'flex', gap: 3, marginBottom: 16 }}>
                  {[...Array(5)].map((_, j) => <Star key={j} size={13} color="#F59E0B" fill="#F59E0B" />)}
                </div>
                <p style={{ fontSize: 14, color: '#CBD5E1', lineHeight: 1.7, marginBottom: 20, flex: 1 }}>
                  "{t.text}"
                </p>
                <div style={{ paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${P}, ${C})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 900, fontSize: 15, color: '#fff' }}>{t.name[0]}</div>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 13, margin: 0 }}>{t.name}</p>
                      <p style={{ color: '#64748B', fontSize: 12, margin: 0 }}>{t.country}</p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ color: G, fontWeight: 800, fontSize: 13, margin: 0 }}>{t.profit}</p>
                    <p style={{ color: '#64748B', fontSize: 11, margin: 0 }}>{t.plan} Plan</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section id="faq" style={{ padding: '100px 24px', borderTop: '1px solid rgba(194,149,0,0.1)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Tag color={P}>FAQ</Tag>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 900, letterSpacing: -1.5,
                margin: '16px 0 0' }}>
                Got <GradientText>Questions?</GradientText>
              </h2>
            </motion.div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FAQS.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                style={{ ...card, overflow: 'hidden', transition: 'border-color .2s',
                  ...(openFaq === i ? { borderColor: `${P}40` } : {}) }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', padding: '18px 20px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', color: '#fff' }}>
                  <span style={{ fontWeight: 700, fontSize: 15, paddingRight: 16 }}>{f.q}</span>
                  <motion.div animate={{ rotate: openFaq === i ? 180 : 0 }} transition={{ duration: 0.2 }}>
                    <ChevronDown size={16} color="#64748B" style={{ flexShrink: 0 }} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                      <p style={{ padding: '0 20px 18px', fontSize: 14, color: '#94A3B8',
                        lineHeight: 1.7, margin: 0, borderTop: '1px solid rgba(255,255,255,0.06)',
                        paddingTop: 14 }}>{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section style={{ padding: '80px 24px 100px', borderTop: '1px solid rgba(194,149,0,0.1)' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            style={{ borderRadius: 24, padding: '64px 40px', textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(194,149,0,0.12) 0%, rgba(6,182,212,0.08) 100%)',
              border: '1px solid rgba(194,149,0,0.25)', position: 'relative', overflow: 'hidden' }}>
            {/* top line */}
            <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
              width: 200, height: 1,
              background: `linear-gradient(90deg, transparent, ${P}, transparent)` }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
              {[[Globe, C], [Wallet, P]].map(([Icon, color], i) => (
                <div key={i} style={{ width: 56, height: 56, borderRadius: 16,
                  background: `linear-gradient(135deg, ${color}30, ${color}15)`,
                  border: `1px solid ${color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 0 24px ${color}25` }}>
                  <Icon size={24} color={color} />
                </div>
              ))}
            </div>

            <h2 style={{ fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 900, letterSpacing: -1.5,
              marginBottom: 16 }}>
              Your Stablecoin Collection<br />
              <GradientText>& Payout Partner</GradientText>
            </h2>
            <p style={{ fontSize: 17, color: '#94A3B8', marginBottom: 36, maxWidth: 500,
              margin: '0 auto 36px', lineHeight: 1.65 }}>
              Accept USDT & USDC globally. Settle to banks and mobile wallets
              across 180+ countries. Fast, secure, built for Africa.
            </p>

            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
              <Link to="/register">
                <motion.button whileHover={{ scale: 1.04, boxShadow: `0 0 48px ${P}50` }}
                  whileTap={{ scale: 0.96 }}
                  style={{ padding: '15px 36px', borderRadius: 12, border: 'none', cursor: 'pointer',
                    background: `linear-gradient(135deg, ${P}, ${C})`,
                    color: '#fff', fontWeight: 800, fontSize: 16,
                    display: 'flex', alignItems: 'center', gap: 8,
                    boxShadow: `0 0 32px ${P}40` }}>
                  Start Free Today <ArrowRight size={18} />
                </motion.button>
              </Link>
              <a href="mailto:api@kudibot.io">
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                  style={{ padding: '15px 36px', borderRadius: 12, cursor: 'pointer',
                    background: 'rgba(194,149,0,0.1)', border: `1px solid ${P}30`,
                    color: '#fff', fontWeight: 700, fontSize: 16,
                    display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Code size={18} color={P2} /> Get API Access
                </motion.button>
              </a>
            </div>
            <p style={{ fontSize: 13, color: '#475569' }}>
              ✓ No credit card &nbsp; ✓ Setup in 10 mins &nbsp; ✓ Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ borderTop: '1px solid rgba(194,149,0,0.1)', padding: '60px 24px 40px',
        background: '#030712' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', gap: 40,
            marginBottom: 48 }} className="grid-cols-footer">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <KudiLogo size={32} />
                <span style={{ fontWeight: 800, fontSize: 17 }}>Kudi<span style={{ color: P2 }}>bot</span></span>
              </div>
              <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.7, marginBottom: 20, maxWidth: 260 }}>
                Your stablecoin collection and payout partner. Collect USDT & USDC. Settle to banks and mobile wallets worldwide.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[[DollarSign, P2, 'USDT & USDC Supported'], [Globe, C, '180+ Countries'], [Lock, G, 'Bank-grade Security']].map(([Icon, color, label], i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#475569' }}>
                    <Icon size={12} color={color} /> {label}
                  </div>
                ))}
              </div>
            </div>
            {[
              { title: 'Payouts', links: [['Nigeria (NGN)','#'],['Ghana (GHS)','#'],['West Africa (XOF)','#'],['Kenya (KES)','#'],['Uganda (UGX)','#'],['180+ Countries','#']] },
              { title: 'Product',  links: [['Collections','#features'],['Global Payouts','#payouts'],['Developer API','#developers'],['Pricing','#pricing'],['Testimonials','#testimonials'],['FAQ','#faq']] },
              { title: 'Company',  links: [['About Us','#'],['Contact Us','mailto:hello@kudibot.io'],['API Access','mailto:api@kudibot.io'],['Telegram','#'],['Privacy Policy','#'],['Terms of Service','#']] },
            ].map((col, i) => (
              <div key={i}>
                <h4 style={{ fontWeight: 700, fontSize: 13, marginBottom: 16, color: '#fff' }}>{col.title}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {col.links.map(([label, href], j) => (
                    <a key={j} href={href} style={{ fontSize: 14, color: '#475569', textDecoration: 'none',
                      transition: 'color .2s' }}
                      onMouseOver={e => e.target.style.color = '#fff'}
                      onMouseOut={e => e.target.style.color = '#475569'}>
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 24,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            justifyContent: 'space-between', gap: 12 }}>
            <p style={{ fontSize: 13, color: '#334155', margin: 0 }}>© 2025 Kudibot. All rights reserved.</p>
            <p style={{ fontSize: 12, color: '#334155', margin: 0 }}>
              Kudibot is a stablecoin collection and payout platform. Always verify recipient details before sending.
            </p>
          </div>
        </div>
      </footer>

      {/* ══ WHATSAPP ══ */}
      <a href="https://wa.me/447860088648?text=Hi%20Kudibot%2C%20I%20want%20to%20learn%20more"
        target="_blank" rel="noopener noreferrer"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
        className="group">
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
          style={{ width: 52, height: 52, borderRadius: '50%',
            background: 'linear-gradient(135deg,#25D366,#128C7E)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(37,211,102,0.4)', position: 'relative' }}>
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'rgba(37,211,102,0.35)',
            animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite' }} />
          <svg viewBox="0 0 32 32" style={{ width: 26, height: 26, fill: 'white', position: 'relative', zIndex: 1 }}>
            <path d="M16.004 2.667C8.637 2.667 2.667 8.637 2.667 16c0 2.347.64 4.64 1.853 6.64L2.667 29.333l6.907-1.813A13.267 13.267 0 0 0 16.004 29.333c7.363 0 13.329-5.97 13.329-13.333S23.367 2.667 16.004 2.667zm0 24c-2.107 0-4.16-.573-5.947-1.653l-.427-.253-4.093 1.08 1.093-4-.28-.44A10.613 10.613 0 0 1 5.333 16c0-5.88 4.787-10.667 10.667-10.667S26.667 10.12 26.667 16 21.88 26.667 16 26.667zm5.84-7.987c-.32-.16-1.893-.933-2.187-1.04-.293-.107-.507-.16-.72.16-.213.32-.827 1.04-.987 1.253-.16.213-.347.24-.667.08-.32-.16-1.347-.493-2.56-1.573-.947-.84-1.587-1.88-1.773-2.2-.187-.32-.02-.493.14-.653.147-.147.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.733-.987-2.373-.253-.613-.52-.533-.72-.547h-.613c-.213 0-.56.08-.853.4-.293.32-1.12 1.093-1.12 2.667s1.147 3.093 1.307 3.307c.16.213 2.253 3.44 5.467 4.827.763.333 1.36.533 1.827.68.773.24 1.467.213 2.013.133.613-.093 1.893-.773 2.16-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.613-.373z"/>
          </svg>
        </motion.div>
        <span style={{ position: 'absolute', right: 60, top: '50%', transform: 'translateY(-50%)',
          background: '#0D1117', color: '#fff', fontSize: 12, fontWeight: 600,
          padding: '6px 12px', borderRadius: 10, border: '1px solid rgba(194,149,0,0.2)',
          whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}
          className="opacity-0 group-hover:opacity-100 transition-opacity">
          Chat with us
        </span>
      </a>

    </div>
  );
}
